import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, BladApi } from '../api/client';
import { useLista } from '../api/lista';
import type { Dostawca, Grupa } from '../api/typy';
import { useAuth } from '../auth/AuthContext';
import {
  Karta, Komunikat, Ladowanie, Plakietka, Pole, Przelacznik, Przycisk, Pusto, Wybor, Zakladki,
} from '../components/ui';
import { PrzyciskExcel } from '../components/PrzyciskExcel';

export function Slowniki() {
  const [zakladka, setZakladka] = useState<'grupy' | 'dostawcy'>('grupy');
  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-kamien-900">Słowniki</h1>
          <p className="mt-1 text-sm text-kamien-500">
            Grupy towarowe porządkują kartotekę, dostawcy wracają na przyjęciach i partiach.
          </p>
        </div>
        <PrzyciskExcel dzial={zakladka} />
      </div>

      <Zakladki
        wartosc={zakladka} zmien={setZakladka}
        opcje={[
          { wartosc: 'grupy', etykieta: 'Grupy towarowe' },
          { wartosc: 'dostawcy', etykieta: 'Dostawcy' },
        ]}
      />

      {zakladka === 'grupy' ? <Grupy /> : <Dostawcy />}
    </div>
  );
}

function Grupy() {
  const { maUprawnienie } = useAuth();
  const mozeEdytowac = maUprawnienie('kartoteka.zapis');
  const qc = useQueryClient();
  const [edytowana, setEdytowana] = useState<Grupa | 'nowa' | null>(null);
  const [dane, setDane] = useState({ code: '', name: '', parentId: '', isActive: true });
  const [bledy, setBledy] = useState<Record<string, string>>({});
  const [blad, setBlad] = useState<string | null>(null);

  const lista = useLista({ queryKey: ['grupy'], queryFn: () => api.get<{ pozycje: Grupa[] }>('/grupy') });

  function otworz(g: Grupa | 'nowa') {
    setEdytowana(g);
    setBledy({}); setBlad(null);
    setDane(g === 'nowa'
      ? { code: '', name: '', parentId: '', isActive: true }
      : { code: g.code, name: g.name, parentId: String(g.parentId ?? ''), isActive: g.isActive });
  }

  const zapisz = useMutation({
    mutationFn: () => {
      const cialo = { ...dane, parentId: dane.parentId || null };
      return edytowana === 'nowa' || !edytowana
        ? api.post('/grupy', cialo)
        : api.put(`/grupy/${edytowana.id}`, { ...cialo, version: edytowana.version });
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['grupy'] }); setEdytowana(null); },
    onError: (e) => {
      if (e instanceof BladApi) {
        setBledy(e.bledyPol);
        if (Object.keys(e.bledyPol).length === 0) setBlad(e.message);
      }
    },
  });

  if (lista.wczytywanie) return <Ladowanie />;

  return (
    <div className="flex flex-col gap-4">
      {mozeEdytowac && (
        <div><Przycisk wariant="glowny" onClick={() => otworz('nowa')}>Nowa grupa</Przycisk></div>
      )}

      {edytowana && (
        <Karta tytul={edytowana === 'nowa' ? 'Nowa grupa' : `Grupa ${edytowana.code}`}>
          <form className="grid gap-4 px-5 py-5 sm:grid-cols-2 lg:grid-cols-4"
            onSubmit={(e) => { e.preventDefault(); zapisz.mutate(); }}>
            {blad && <div className="sm:col-span-2 lg:col-span-4"><Komunikat rodzaj="blad">{blad}</Komunikat></div>}
            <Pole etykieta="Kod" value={dane.code} blad={bledy.code}
              onChange={(e) => setDane({ ...dane, code: e.target.value })} required />
            <Pole etykieta="Nazwa" value={dane.name} blad={bledy.name}
              onChange={(e) => setDane({ ...dane, name: e.target.value })} required />
            <Wybor etykieta="Grupa nadrzędna" value={dane.parentId} blad={lista.blad}
              onChange={(e) => setDane({ ...dane, parentId: e.target.value })}>
              <option value="">— brak —</option>
              {lista.pozycje
                .filter((g) => edytowana === 'nowa' || g.id !== edytowana.id)
                .map((g) => <option key={g.id} value={g.id}>{g.code} — {g.name}</option>)}
            </Wybor>
            <div className="flex items-end">
              <Przelacznik etykieta="Grupa czynna" checked={dane.isActive}
                onChange={(e) => setDane({ ...dane, isActive: e.target.checked })} />
            </div>
            <div className="flex gap-3 sm:col-span-2 lg:col-span-4">
              <Przycisk type="submit" wariant="glowny" disabled={zapisz.isPending}>Zapisz</Przycisk>
              <Przycisk type="button" wariant="cichy" onClick={() => setEdytowana(null)}>Anuluj</Przycisk>
            </div>
          </form>
        </Karta>
      )}

      <Karta>
        {lista.pozycje.length > 0 ? (
          <ul className="divide-y divide-kamien-200">
            {lista.pozycje.map((g) => (
              <li key={g.id} className="flex items-center gap-3 px-5 py-3">
                <span className="font-mono text-xs text-kamien-500">{g.code}</span>
                <span className={`flex-1 text-sm ${g.isActive ? 'text-kamien-900' : 'text-kamien-400'}`}>
                  {g.name}
                  {g.parent && <span className="ml-2 text-xs text-kamien-400">w: {g.parent.name}</span>}
                </span>
                <Plakietka>{g._count.items} pozycji</Plakietka>
                {!g.isActive && <Plakietka>wyłączona</Plakietka>}
                {mozeEdytowac && (
                  <Przycisk wariant="cichy" className="min-h-9 px-2 text-xs" onClick={() => otworz(g)}>
                    Edytuj
                  </Przycisk>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <Pusto tytul="Brak grup" opis="Grupy nie są obowiązkowe, ale porządkują kartotekę i raporty." />
        )}
      </Karta>
    </div>
  );
}

function Dostawcy() {
  const { maUprawnienie } = useAuth();
  const mozeEdytowac = maUprawnienie('dostawcy.zapis');
  const qc = useQueryClient();
  const [edytowany, setEdytowany] = useState<Dostawca | 'nowy' | null>(null);
  const [szukaj, setSzukaj] = useState('');
  const [dane, setDane] = useState({
    code: '', name: '', taxId: '', phone: '', email: '', address: '', notes: '', isActive: true,
  });
  const [bledy, setBledy] = useState<Record<string, string>>({});
  const [blad, setBlad] = useState<string | null>(null);

  const lista = useQuery({
    queryKey: ['dostawcy', szukaj],
    queryFn: () => api.get<{ pozycje: Dostawca[] }>(
      `/dostawcy${szukaj ? `?szukaj=${encodeURIComponent(szukaj)}` : ''}`,
    ),
  });

  function otworz(d: Dostawca | 'nowy') {
    setEdytowany(d);
    setBledy({}); setBlad(null);
    setDane(d === 'nowy'
      ? { code: '', name: '', taxId: '', phone: '', email: '', address: '', notes: '', isActive: true }
      : {
          code: d.code, name: d.name, taxId: d.taxId ?? '', phone: d.phone ?? '',
          email: d.email ?? '', address: d.address ?? '', notes: d.notes ?? '', isActive: d.isActive,
        });
  }

  const zapisz = useMutation({
    mutationFn: () =>
      edytowany === 'nowy' || !edytowany
        ? api.post('/dostawcy', dane)
        : api.put(`/dostawcy/${edytowany.id}`, { ...dane, version: edytowany.version }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['dostawcy'] }); setEdytowany(null); },
    onError: (e) => {
      if (e instanceof BladApi) {
        setBledy(e.bledyPol);
        if (Object.keys(e.bledyPol).length === 0) setBlad(e.message);
      }
    },
  });

  if (lista.isLoading) return <Ladowanie />;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-64 max-w-md flex-1">
          <Pole etykieta="Szukaj" value={szukaj} onChange={(e) => setSzukaj(e.target.value)}
            placeholder="Nazwa, kod albo NIP" />
        </div>
        {mozeEdytowac && <Przycisk wariant="glowny" onClick={() => otworz('nowy')}>Nowy dostawca</Przycisk>}
      </div>

      {edytowany && (
        <Karta tytul={edytowany === 'nowy' ? 'Nowy dostawca' : `Dostawca ${edytowany.code}`}>
          <form className="grid gap-4 px-5 py-5 sm:grid-cols-2 lg:grid-cols-3"
            onSubmit={(e) => { e.preventDefault(); zapisz.mutate(); }}>
            {blad && <div className="sm:col-span-2 lg:col-span-3"><Komunikat rodzaj="blad">{blad}</Komunikat></div>}
            <Pole etykieta="Kod" value={dane.code} blad={bledy.code}
              onChange={(e) => setDane({ ...dane, code: e.target.value })} required />
            <div className="lg:col-span-2">
              <Pole etykieta="Nazwa" value={dane.name} blad={bledy.name}
                onChange={(e) => setDane({ ...dane, name: e.target.value })} required />
            </div>
            <Pole etykieta="NIP" value={dane.taxId} blad={bledy.taxId}
              onChange={(e) => setDane({ ...dane, taxId: e.target.value })} />
            <Pole etykieta="Telefon" value={dane.phone} blad={bledy.phone}
              onChange={(e) => setDane({ ...dane, phone: e.target.value })} />
            <Pole etykieta="E-mail" type="email" value={dane.email} blad={bledy.email}
              onChange={(e) => setDane({ ...dane, email: e.target.value })} />
            <div className="sm:col-span-2 lg:col-span-3">
              <Pole etykieta="Adres" value={dane.address} blad={bledy.address}
                onChange={(e) => setDane({ ...dane, address: e.target.value })} />
            </div>
            <div className="sm:col-span-2 lg:col-span-3">
              <Przelacznik etykieta="Dostawca czynny" checked={dane.isActive}
                onChange={(e) => setDane({ ...dane, isActive: e.target.checked })} />
            </div>
            <div className="flex gap-3 sm:col-span-2 lg:col-span-3">
              <Przycisk type="submit" wariant="glowny" disabled={zapisz.isPending}>Zapisz</Przycisk>
              <Przycisk type="button" wariant="cichy" onClick={() => setEdytowany(null)}>Anuluj</Przycisk>
            </div>
          </form>
        </Karta>
      )}

      <Karta>
        {lista.data && lista.data.pozycje.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead className="border-b border-kamien-200 text-left text-xs uppercase tracking-wide text-kamien-500">
                <tr>
                  <th className="px-5 py-3 font-medium">Kod</th>
                  <th className="px-5 py-3 font-medium">Nazwa</th>
                  <th className="px-5 py-3 font-medium">NIP</th>
                  <th className="px-5 py-3 font-medium">Kontakt</th>
                  <th className="px-5 py-3 font-medium">Powiązania</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-kamien-200">
                {lista.data.pozycje.map((d) => (
                  <tr key={d.id} onClick={() => mozeEdytowac && otworz(d)}
                    className={`${mozeEdytowac ? 'cursor-pointer hover:bg-kamien-50' : ''} ${d.isActive ? '' : 'text-kamien-400'}`}>
                    <td className="px-5 py-3 font-mono text-xs">{d.code}</td>
                    <td className="px-5 py-3 text-kamien-900">{d.name}</td>
                    <td className="px-5 py-3 text-kamien-600">{d.taxId ?? '—'}</td>
                    <td className="px-5 py-3 text-xs text-kamien-500">
                      {[d.phone, d.email].filter(Boolean).join(' · ') || '—'}
                    </td>
                    <td className="px-5 py-3 text-xs text-kamien-500">
                      {d._count.equipmentUnits} egz. · {d._count.batches} partii
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Pusto tytul="Brak dostawców" opis="Dostawcy przydadzą się przy przyjęciach i partiach materiałów." />
        )}
      </Karta>
    </div>
  );
}
